/**
 * Lengths, rendered the way a tape measure reads them.
 *
 * This is a hand-written mirror of the engine's `format.hpp`, and it exists because
 * two outputs on the JS side carry raw sixteenths that a person has to read: the bill
 * of materials and the cut list. Both get printed on a sheet beside elevations the
 * *engine* dimensioned, so `7'-6 1/2"` has to mean the same thing and look the same
 * on both halves of the page. A formatter that rounded to two decimals would put two
 * different notations for one length in one document.
 *
 * It lives here rather than in either consumer for the reason `inches()` and `feet()`
 * do: this package owns the `Sixteenths` type, and how that type reads is part of
 * owning it. `test/format.test.ts` holds it to the same table the engine's own
 * `test_format.cpp` asserts.
 *
 * Integer arithmetic only, and no locale. Fractions reduce against sixteen, so a
 * remainder of eight prints as `1/2` and never as `8/16`.
 */

import { SIXTEENTHS_PER_INCH, type Sixteenths } from "./types.js";

function greatestCommonDivisor(a: number, b: number): number {
  let x = a;
  let y = b;
  while (y !== 0) {
    [x, y] = [y, x % y];
  }
  return x;
}

/** The remainder as a reduced fraction with a leading space: `8` becomes `" 1/2"`. */
function fractionPart(sixteenths: number): string {
  if (sixteenths === 0) {
    return "";
  }
  const divisor = greatestCommonDivisor(sixteenths, SIXTEENTHS_PER_INCH);
  return ` ${sixteenths / divisor}/${SIXTEENTHS_PER_INCH / divisor}`;
}

/** Architectural feet and inches: `7'-6 1/2"`, `8'-0"`, `6 1/4"`, `0"`. */
export function toFeetInches(length: Sixteenths | number): string {
  const sign = length < 0 ? "-" : "";
  const magnitude = Math.abs(length);

  const wholeInches = Math.floor(magnitude / SIXTEENTHS_PER_INCH);
  const fraction = fractionPart(magnitude % SIXTEENTHS_PER_INCH);

  const feet = Math.floor(wholeInches / 12);
  const inches = wholeInches % 12;

  if (feet === 0) {
    return `${sign}${inches}${fraction}"`;
  }
  return `${sign}${feet}'-${inches}${fraction}"`;
}

/** Inches with a reduced fraction, never rolled up into feet: `90 1/2"`. */
export function toInches(length: Sixteenths | number): string {
  const sign = length < 0 ? "-" : "";
  const magnitude = Math.abs(length);
  return `${sign}${Math.floor(magnitude / SIXTEENTHS_PER_INCH)}${fractionPart(
    magnitude % SIXTEENTHS_PER_INCH,
  )}"`;
}

/**
 * Board feet from the thousandths the bill counts in: `5333` reads `5.33`.
 *
 * Rounded in integers, and that is the whole of the care here. `(t / 1000).toFixed(2)`
 * divides into a binary float first, so a value whose exact decimal sits on a midpoint
 * can land just below it: 15 thousandths is 0.015, which is stored as very slightly
 * less than 0.015 and rounds *down* to "0.01". Roughly one value in twenty is a penny
 * short that way, and two of the eleven specs in the parity corpus hit it on their
 * whole-building total — which prints on the free preview, because aggregates are not
 * withheld.
 *
 * The tenths digit decides, nothing is ever a float, and the result is the same
 * argument `Sixteenths` makes everywhere else in the engine: integers all the way to
 * the string.
 */
export function toBoardFeet(thousandths: number): string {
  const sign = thousandths < 0 ? "-" : "";
  const magnitude = Math.abs(Math.trunc(thousandths));
  const hundredths = Math.floor(magnitude / 10) + (magnitude % 10 >= 5 ? 1 : 0);
  return `${sign}${Math.floor(hundredths / 100)}.${String(hundredths % 100).padStart(2, "0")}`;
}
